import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu as MenuIcon, X, LogOut } from 'lucide-react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { LoadingIndicator } from './LoadingIndicator';
import { ADMIN_EMAIL } from '../lib/constants';
import { clearAllToolDrafts } from '../utils/toolSessionStore';

interface MenuItem {
  id: string;
  label: string;
  url: string;
  order: number;
  parent_id: string | null;
}

interface HeaderProps {
  menuItems?: MenuItem[];
  logo?: string;
  isLoading?: boolean;
}

export function Header({ menuItems: propMenuItems, logo: propLogo, isLoading: propLoading }: HeaderProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();

  const isAdmin = session?.user?.email === ADMIN_EMAIL;
  const menuItems = propMenuItems || [];
  const logo = propLogo || '';
  const loading = propLoading ?? false;

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    // Only add the event listener when dropdown is open
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = async () => {
    if (signingOut) return;
    
    setSigningOut(true);
    try {
      const consent = localStorage.getItem('cookie_consent');
      localStorage.clear();
      if (consent) localStorage.setItem('cookie_consent', consent);
      sessionStorage.clear();
      await clearAllToolDrafts();
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error during sign out:', error);
        // Even if there's an error, we should still redirect
      }
      
      // Force a page reload to clear any cached state
      window.location.href = '/';
    } catch (error) {
      console.error('Error during sign out:', error);
      // Force redirect even if there's an error
      window.location.href = '/';
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-20 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-full">
          <Link to="/" className="flex items-center">
            {loading ? (
              <div className="h-16 w-40 flex items-center">
                <LoadingIndicator size="sm" fullPage={false} />
              </div>
            ) : logo ? (
              <img src={logo} alt="Logo" className="h-16 w-auto" />
            ) : (
              <span className="text-2xl font-bold text-primary-600">Merchant Floor</span>
            )}
          </Link>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-1.5 text-gray-700 hover:text-gray-900"
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <MenuIcon className="w-5 h-5" />
            )}
          </button>

          <div className="hidden lg:flex items-center space-x-6">
            <nav>
              <ul className="flex items-center space-x-1">
                <li className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                  >
                    <span>Apps</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg py-2 z-50">
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Listing Tool
                      </div>
                      <Link
                        to="/product-listing-agent"
                        className="block px-3 py-1.5 text-sm font-medium text-gray-500  hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Product Listing Agent
                      </Link>
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider mt-2">
                        Image Tools
                      </div>
                      <Link
                        to="/photo-studio"
                        className="block px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        AI Product Photo Studio
                      </Link>
                      <Link
                        to="/lifestyle-image-generator"
                        className="block px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        AI Lifestyle Product Photos
                      </Link>
                      <Link
                        to="/remove-background"
                        className="block px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        AI Background Remover
                      </Link>
                      <Link
                        to="/image-resizer"
                        className="block px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Image Resizer (Free)
                      </Link>

                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider mt-2">
                        Copy Tools
                      </div>
                      <Link
                        to="/product-description"
                        className="block px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        AI Product Description Writer
                      </Link>
                      <Link
                        to="/social-media"
                        className="block px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        AI Social Post Writer
                      </Link>
                      <Link
                        to="/blog-writer"
                        className="block px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        AI Blog Writer
                      </Link>
                    </div>
                  )}
                </li>
                
                {loading ? (
                  <li><LoadingIndicator size="sm" fullPage={false} /></li>
                ) : (
                  menuItems.map((item) => (
                    <li key={item.id}>
                      <Link 
                        to={item.url} 
                        className={`px-3 py-2 text-sm rounded-lg ${
                          location.pathname === item.url 
                            ? 'text-gray-900 bg-gray-100' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))
                )}
                <li>
                  <Link
                    to="/demos"
                    className={`px-3 py-2 text-sm rounded-lg ${location.pathname === '/demos' ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                  >
                    Demos
                  </Link>
                </li>
              </ul>
            </nav>

            {session ? (
              <div className="flex items-center space-x-1">
                <Link
                  to="/dashboard"
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                >
                  Dashboard
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  disabled={signingOut}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1"
                >
                  {signingOut ? (
                    <LoadingIndicator size="sm" fullPage={false} />
                  ) : (
                    <>
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 text-sm site-btn-primary transition-colors"
                >
                  Try it free
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden fixed inset-0 z-50 bg-white transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full overflow-y-auto">
            <div className="px-4 py-6">
              <div className="flex justify-between items-center mb-6">
                <Link to="/" className="flex items-center">
                  {loading ? (
                    <div className="h-16 w-40 flex items-center">
                      <LoadingIndicator size="sm" fullPage={false} />
                    </div>
                  ) : logo ? (
                    <img src={logo} alt="Logo" className="h-16 w-auto" />
                  ) : (
                    <span className="text-2xl font-bold text-primary-600">Merchant Floor</span>
                  )}
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1.5 text-gray-700 hover:text-gray-900"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <nav className="space-y-4">
                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Listing Tool
                  </div>
                  <Link
                    to="/product-listing-agent"
                    className="block px-3 py-2 text-sm font-medium text-gray-500 hover:text-primary-700 rounded-lg hover:bg-gray-100"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Product Listing Agent
                  </Link>
                </div>

                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Image Tools
                  </div>
                  <ul className="space-y-1">
                    <li>
                      <Link
                        to="/photo-studio"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        AI Product Photo Studio
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/lifestyle-image-generator"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        AI Lifestyle Product Photos
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/remove-background"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        AI Background Remover
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/image-resizer"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        Image Resizer (Free)
                      </Link>
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Copy Tools
                  </div>
                  <ul className="space-y-1">
                    <li>
                      <Link
                        to="/product-description"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        AI Product Description Writer
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/social-media"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        AI Social Post Writer
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/blog-writer"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        AI Blog Writer
                      </Link>
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                    Menu
                  </div>
                  <ul className="space-y-1">
                    {menuItems.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={item.url}
                          className={`block px-3 py-2 text-sm rounded-lg ${
                            location.pathname === item.url
                              ? 'text-gray-900 bg-gray-100'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link
                        to="/demos"
                        className={`block px-3 py-2 text-sm rounded-lg ${location.pathname === '/demos' ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                      >
                        Demos
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  {session ? (
                    <div className="space-y-1">
                      <Link
                        to="/dashboard"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        Dashboard
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                        >
                          Admin
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        disabled={signingOut}
                        className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                      >
                        {signingOut ? 'Signing Out...' : 'Sign Out'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Link
                        to="/login"
                        className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/signup"
                        className="block px-3 py-2 text-sm site-btn-primary transition-colors"
                      >
                        Try it free
                      </Link>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}